import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { GraduationCap, Mail, Lock, User, Calendar, Award, BookOpen, Sparkles, ArrowRight, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupRole, setSignupRole] = useState('student');
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [grade, setGrade] = useState('');
  const [institutionKey, setInstitutionKey] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      toast.success('Login successful!');
      
      // Extract user data and token from response
      const { access_token, user } = response.data;
      onLogin(user, access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        email: signupEmail,
        password: signupPassword,
        role: signupRole,
        name,
        age: parseInt(age),
        grade,
      };
      
      if (signupRole === 'superuser' && institutionKey) {
        payload.institution_key = institutionKey;
      }

      const response = await axios.post(`${API}/signup`, payload);
      toast.success('Signup successful! Redirecting...');
      
      // Extract user data and token from response
      const { access_token, user } = response.data;
      onLogin(user, access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero Section with Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1530825894095-9c184b068fcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxsZWFybmluZ3xlbnwwfHx8Ymx1ZXwxNzYxMDQ3Mzg4fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/85 to-indigo-900/90" />
        
        <div className="relative z-10 flex flex-col justify-center items-start p-12 text-white max-w-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
              <GraduationCap size={40} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Buddy AI</h1>
              <p className="text-blue-200 text-sm">Your AI-Powered Learning Companion</p>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            Transform Your Learning Journey with AI
          </h2>
          
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            Experience personalized education with intelligent tutoring, adaptive quizzes, and comprehensive progress tracking.
          </p>

          {/* Feature highlights */}
          <div className="space-y-4 w-full">
            <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <div className="p-2 bg-green-500 rounded-lg">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-semibold mb-1">AI-Powered Personalization</h3>
                <p className="text-sm text-blue-100">Adaptive learning paths tailored to your grade and goals</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Award size={20} />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Comprehensive Reporting</h3>
                <p className="text-sm text-blue-100">Detailed insights and performance analytics</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <div className="p-2 bg-pink-500 rounded-lg">
                <Shield size={20} />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Multi-Role Support</h3>
                <p className="text-sm text-blue-100">Students, Parents, Teachers, and Supervisors</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      {/* Right side - Login/Signup Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <Card className="w-full max-w-md p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
          <div className="mb-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
              <BookOpen size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Welcome Back!
            </h2>
            <p className="text-gray-600 mt-2">Continue your learning journey</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">
                <Mail size={16} className="mr-2" />
                Login
              </TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">
                <User size={16} className="mr-2" />
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="flex items-center gap-2 text-gray-700">
                    <Mail size={16} />
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="student@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="mt-2 border-gray-300 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="flex items-center gap-2 text-gray-700">
                    <Lock size={16} />
                    Password
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="mt-2 border-gray-300 focus:border-blue-500"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? 'Logging in...' : (
                    <span className="flex items-center justify-center gap-2">
                      Login
                      <ArrowRight size={18} />
                    </span>
                  )}
                </Button>
              </form>
              
              <div className="mt-6 space-y-2">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">For Institutions</span>
                  </div>
                </div>
                <div className="flex gap-2 text-sm">
                  <a href="/institution-register" className="text-blue-600 hover:text-blue-800 flex-1 text-center py-2 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                    Register Institution
                  </a>
                  <a href="/institution-login" className="text-purple-600 hover:text-purple-800 flex-1 text-center py-2 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors">
                    Institution Login
                  </a>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-name" className="flex items-center gap-2 text-gray-700">
                    <User size={16} />
                    Full Name
                  </Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-email" className="flex items-center gap-2 text-gray-700">
                    <Mail size={16} />
                    Email
                  </Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="student@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password" className="flex items-center gap-2 text-gray-700">
                    <Lock size={16} />
                    Password
                  </Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="signup-age" className="flex items-center gap-2 text-gray-700">
                      <Calendar size={16} />
                      Age
                    </Label>
                    <Input
                      id="signup-age"
                      type="number"
                      placeholder="15"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signup-grade" className="flex items-center gap-2 text-gray-700">
                      <GraduationCap size={16} />
                      Grade
                    </Label>
                    <Input
                      id="signup-grade"
                      type="text"
                      placeholder="10th"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="signup-role" className="flex items-center gap-2 text-gray-700">
                    <Award size={16} />
                    Role
                  </Label>
                  <select
                    id="signup-role"
                    value={signupRole}
                    onChange={(e) => setSignupRole(e.target.value)}
                    className="mt-2 w-full p-2 border rounded-lg bg-white"
                  >
                    <option value="student">Student</option>
                    <option value="parent">Parent</option>
                    <option value="teacher">Teacher</option>
                    <option value="counselor">Counselor</option>
                    <option value="superuser">Supervisor</option>
                  </select>
                </div>
                {signupRole === 'superuser' && (
                  <div>
                    <Label htmlFor="institution-key" className="flex items-center gap-2 text-gray-700">
                      <Shield size={16} />
                      Institution Key
                    </Label>
                    <Input
                      id="institution-key"
                      type="text"
                      placeholder="Enter institution key"
                      value={institutionKey}
                      onChange={(e) => setInstitutionKey(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                )}
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? 'Creating Account...' : (
                    <span className="flex items-center justify-center gap-2">
                      Create Account
                      <ArrowRight size={18} />
                    </span>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;